/**
 * The game-facing audio API.
 *
 * Everything the match can make a noise about goes through one object: the
 * continuous engine bed driven per frame from car state, and a set of one-shots
 * for the discrete events. There are no samples — each of these is a small
 * node graph assembled on the spot from the primitives in ./synth.
 *
 * Three things shape the code more than anything else:
 *
 *   - NO ASSETS. A sound is a spectrum plus an envelope plus a filter move, and
 *     the filter move is usually what carries the meaning. A ball hit is not "a
 *     louder tick" than a soft touch, it is lower, brighter and longer at once.
 *
 *   - VOICE LIMITING. A corner pinch can fire a dozen contacts in three frames.
 *     Uncapped that is forty oscillators fighting the limiter; capped, it is a
 *     hit. New voices steal the quietest voice that is closest to finishing.
 *
 *   - IMPORT SAFETY. Nothing here runs at import time and every method starts
 *     by asking the mixer for a graph. In Node — or before the first user
 *     gesture — that returns null and every call is a silent no-op, which is
 *     why this module can be imported by a test that has no AudioContext.
 *
 * Positions are physics-frame Vec3 in uu and are converted through
 * src/render/convert.ts, the project's single frame boundary, because the
 * Web Audio listener is the Three.js camera.
 */

import type { CarInput, CarState } from '../physics/car-state';
import { length, type Vec3 } from '../physics/math';
import { toThree } from '../render/convert';
import { Mixer, sharedMixer } from './mixer';
import {
  boostPickupTone,
  countdownTone,
  ENGINE_PARTIALS,
  ENGINE_TOP_SPEED,
  engineTone,
  impactParams,
  impactStrength,
  loopableNoise,
  makeRng,
  type NoiseKind,
  type Rng,
} from './synth';

// ---------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------

/** Simultaneous one-shots. Past this, new sounds steal rather than stack. */
const DEFAULT_MAX_VOICES = 16;

/** Length of the shared noise buffers. Long enough that a loop is not heard. */
const NOISE_SECONDS = 2;

/** Crossfade at the noise loop seam, in samples. */
const NOISE_LOOP_FADE = 512;

/**
 * Exponential ramps cannot reach zero, so envelopes bottom out here and then
 * take a hard step to silence. -80 dB is below anything audible under a mix.
 */
const FLOOR = 1e-4;

/** Smoothing time constant for the per-frame engine parameter updates. */
const ENGINE_SMOOTH = 0.06;

/** Fade applied to a stolen voice. Long enough not to click, short enough not
 *  to be heard as part of the new sound. */
const STEAL_FADE = 0.012;

// ---------------------------------------------------------------------------
// Voices
// ---------------------------------------------------------------------------

interface Voice {
  id: number;
  /** Nominal peak level, used to rank steal candidates. */
  level: number;
  startedAt: number;
  endsAt: number;
  /** Where the sound connects. */
  out: GainNode;
  /** Panner between `out` and the bus, when the sound is positioned. */
  panner: PannerNode | null;
  sources: AudioScheduledSourceNode[];
  released: boolean;
}

/** Everything a sound needs to build itself into an allocated voice. */
interface Shot {
  ctx: AudioContext;
  /** Scheduling origin. Always slightly ahead of `currentTime`. */
  t0: number;
  /** Connect sources here. */
  out: GainNode;
  voice: Voice;
}

/** The continuous per-car layers, which share one panner. */
interface Rig {
  ctx: AudioContext;
  input: GainNode;
  panner: PannerNode;

  /** Dark, lope-heavy bank; dominant at idle. */
  oscIdle: OscillatorNode;
  gainIdle: GainNode;
  /** Bright, strained bank; dominant under load. */
  oscLoad: OscillatorNode;
  gainLoad: GainNode;
  lowpass: BiquadFilterNode;
  engineGain: GainNode;

  /** Filtered noise bed standing in for combustion roughness. */
  roughSrc: AudioBufferSourceNode;
  roughFilter: BiquadFilterNode;
  roughGain: GainNode;

  boost: BoostLayer | null;
  supersonic: SupersonicLayer | null;
}

interface BoostLayer {
  hissSrc: AudioBufferSourceNode;
  hiss: BiquadFilterNode;
  rumbleSrc: AudioBufferSourceNode;
  rumble: BiquadFilterNode;
  gain: GainNode;
  stopping: boolean;
}

interface SupersonicLayer {
  src: AudioBufferSourceNode;
  band: BiquadFilterNode;
  whistle: OscillatorNode;
  whistleGain: GainNode;
  gain: GainNode;
  stopping: boolean;
}

export interface AudioEngineOptions {
  mixer?: Mixer;
  /** Seed for every noise buffer and every randomized detune. */
  seed?: number;
  maxVoices?: number;
}

// ---------------------------------------------------------------------------

export class AudioEngine {
  readonly mixer: Mixer;

  private readonly maxVoices: number;
  private readonly seed: number;
  private readonly rng: Rng;

  private voices: Voice[] = [];
  private nextVoiceId = 1;

  private buffers = new Map<string, AudioBuffer>();
  private rig: Rig | null = null;

  /** Slow random walk on engine pitch. A perfectly steady fundamental is the
   *  single biggest giveaway that an engine is synthesized. */
  private jitter = 0;

  constructor(options: AudioEngineOptions = {}) {
    this.mixer = options.mixer ?? sharedMixer();
    this.maxVoices = Math.max(1, options.maxVoices ?? DEFAULT_MAX_VOICES);
    this.seed = options.seed ?? 0x5eed;
    this.rng = makeRng(this.seed);
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  /** Call from the first user gesture. See Mixer.resume. */
  resume(): Promise<boolean> {
    return this.mixer.resume();
  }

  setMuted(muted: boolean): void {
    this.mixer.setMuted(muted);
  }

  isMuted(): boolean {
    return this.mixer.isMuted();
  }

  /** Live one-shot count. Exposed mainly so the debug overlay can show it. */
  get activeVoices(): number {
    return this.voices.length;
  }

  dispose(): void {
    for (const v of [...this.voices]) this.release(v);
    this.teardownRig();
    this.buffers.clear();
  }

  // -------------------------------------------------------------------------
  // Listener
  // -------------------------------------------------------------------------

  /**
   * Point the listener at the camera. `position` is a physics-frame point;
   * `forward` and `up` are physics-frame directions.
   *
   * The modern AudioListener exposes AudioParams; Safari still only has the
   * deprecated setter, so both paths are kept.
   */
  setListener(position: Vec3, forward: Vec3, up: Vec3): void {
    const buses = this.mixer.ensure();
    if (!buses) return;
    const l = buses.ctx.listener;
    const t = this.mixer.now;

    const p = toThree(position);
    const f = toThree(forward);
    const u = toThree(up);

    const px = (l as unknown as { positionX?: AudioParam }).positionX;
    if (px) {
      const a = l as unknown as Record<string, AudioParam>;
      // setTargetAtTime rather than setValueAtTime: the camera moves every
      // frame and stepping the listener produces zipper noise on the panners.
      a.positionX.setTargetAtTime(p.x, t, 0.02);
      a.positionY.setTargetAtTime(p.y, t, 0.02);
      a.positionZ.setTargetAtTime(p.z, t, 0.02);
      a.forwardX.setTargetAtTime(f.x, t, 0.02);
      a.forwardY.setTargetAtTime(f.y, t, 0.02);
      a.forwardZ.setTargetAtTime(f.z, t, 0.02);
      a.upX.setTargetAtTime(u.x, t, 0.02);
      a.upY.setTargetAtTime(u.y, t, 0.02);
      a.upZ.setTargetAtTime(u.z, t, 0.02);
      return;
    }

    const legacy = l as unknown as {
      setPosition?: (x: number, y: number, z: number) => void;
      setOrientation?: (fx: number, fy: number, fz: number, ux: number, uy: number, uz: number) => void;
    };
    legacy.setPosition?.(p.x, p.y, p.z);
    legacy.setOrientation?.(f.x, f.y, f.z, u.x, u.y, u.z);
  }

  // -------------------------------------------------------------------------
  // Continuous engine
  // -------------------------------------------------------------------------

  /**
   * Drive the engine bed from car state. Call once per rendered frame.
   *
   * `input` is optional because CarState carries no controller input; without
   * it the sound follows speed alone, which is right for remote cars but loses
   * the throttle blip you want on the car you are driving.
   */
  updateEngine(car: CarState, dt: number, input?: CarInput): void {
    const rig = this.ensureRig();
    if (!rig) return;

    const speed = length(car.velocity);
    const throttle = input ? input.throttle : 0;
    const boosting = !!input?.boost && car.boostAmount > 0;
    const tone = engineTone(throttle, speed, boosting);

    // Random walk, damped so it wanders rather than drifts away. The scale is
    // deliberately small: this is mechanical unsteadiness, not vibrato.
    const step = Math.max(0, Math.min(0.1, Number.isFinite(dt) ? dt : 0));
    this.jitter = this.jitter * (1 - 3 * step) + (this.rng() * 2 - 1) * 90 * step;
    this.jitter = Math.max(-14, Math.min(14, this.jitter));

    const t = this.mixer.now;
    const set = (p: AudioParam, v: number): void => {
      p.setTargetAtTime(v, t, ENGINE_SMOOTH);
    };

    // Both banks run at half the fundamental: the periodic waves are built on
    // the half-order partial, so partial index 2 is the fundamental proper.
    const half = tone.fundamental * 0.5;
    set(rig.oscIdle.frequency, half);
    set(rig.oscLoad.frequency, half);
    set(rig.oscIdle.detune, this.jitter);
    set(rig.oscLoad.detune, tone.detuneCents + this.jitter * 0.7);

    // Equal-power crossfade between the two timbres, so the total stays put.
    const x = tone.load;
    set(rig.gainIdle.gain, Math.cos((x * Math.PI) / 2));
    set(rig.gainLoad.gain, Math.sin((x * Math.PI) / 2));

    set(rig.lowpass.frequency, tone.lowpassHz);
    set(rig.roughFilter.frequency, 180 + tone.fundamental * 1.6);
    set(rig.roughGain.gain, tone.noiseLevel);

    // A demolished car makes no noise; neither does one that has not spawned.
    const alive = !car.demolished;
    set(rig.engineGain.gain, alive ? tone.gain : 0);

    this.setPannerPosition(rig.panner, car.position, t, true);
  }

  /** Silence the engine bed without tearing the nodes down. */
  stopEngine(): void {
    const rig = this.rig;
    if (!rig) return;
    rig.engineGain.gain.setTargetAtTime(0, this.mixer.now, 0.08);
  }

  // -------------------------------------------------------------------------
  // Boost / supersonic — sustained, gated layers
  // -------------------------------------------------------------------------

  /**
   * The boost flame: a bandpassed hiss for the jet and a lowpassed bed for the
   * body of it. Both come from the shared white-noise loop at different
   * playback rates so the two layers do not correlate into a single hiss.
   */
  playBoost(active: boolean): void {
    const rig = this.ensureRig();
    if (!rig) return;
    const ctx = rig.ctx;
    const t = this.mixer.now;

    if (!active) {
      const layer = rig.boost;
      if (!layer || layer.stopping) return;
      layer.stopping = true;
      layer.gain.gain.cancelScheduledValues(t);
      layer.gain.gain.setValueAtTime(layer.gain.gain.value, t);
      layer.gain.gain.linearRampToValueAtTime(0, t + 0.14);
      // The filter opening back up as it dies is the "release" of the jet.
      layer.hiss.frequency.setTargetAtTime(900, t, 0.06);
      const stopAt = t + 0.2;
      layer.hissSrc.stop(stopAt);
      layer.rumbleSrc.stop(stopAt);
      layer.hissSrc.onended = () => {
        layer.gain.disconnect();
        if (rig.boost === layer) rig.boost = null;
      };
      return;
    }

    if (rig.boost && !rig.boost.stopping) return;
    if (rig.boost) this.killBoost(rig);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.55, t + 0.05);
    gain.connect(rig.input);

    const hissSrc = this.noiseSource(ctx, 'white', 1.7);
    const hiss = ctx.createBiquadFilter();
    hiss.type = 'bandpass';
    hiss.Q.value = 0.9;
    // Sweeping up from a closed filter is the ignition; without it the boost
    // simply appears at full brightness and sounds like a switch.
    hiss.frequency.setValueAtTime(900, t);
    hiss.frequency.exponentialRampToValueAtTime(2600, t + 0.12);
    hissSrc.connect(hiss).connect(gain);

    const rumbleSrc = this.noiseSource(ctx, 'pink', 0.7);
    const rumble = ctx.createBiquadFilter();
    rumble.type = 'lowpass';
    rumble.frequency.value = 260;
    rumble.Q.value = 1.4;
    rumbleSrc.connect(rumble).connect(gain);

    hissSrc.start(t);
    rumbleSrc.start(t);

    rig.boost = { hissSrc, hiss, rumbleSrc, rumble, gain, stopping: false };
  }

  /**
   * Supersonic: wind roar plus a thin whistle riding above it. The whistle is
   * what makes it read as speed rather than as noise, and it is detuned slowly
   * so it never sits still.
   */
  playSupersonic(on: boolean): void {
    const rig = this.ensureRig();
    if (!rig) return;
    const ctx = rig.ctx;
    const t = this.mixer.now;

    if (!on) {
      const layer = rig.supersonic;
      if (!layer || layer.stopping) return;
      layer.stopping = true;
      layer.gain.gain.cancelScheduledValues(t);
      layer.gain.gain.setValueAtTime(layer.gain.gain.value, t);
      layer.gain.gain.linearRampToValueAtTime(0, t + 0.35);
      const stopAt = t + 0.4;
      layer.src.stop(stopAt);
      layer.whistle.stop(stopAt);
      layer.src.onended = () => {
        layer.gain.disconnect();
        if (rig.supersonic === layer) rig.supersonic = null;
      };
      return;
    }

    if (rig.supersonic && !rig.supersonic.stopping) return;
    if (rig.supersonic) this.killSupersonic(rig);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.4, t + 0.25);
    gain.connect(rig.input);

    const src = this.noiseSource(ctx, 'pink', 1.25);
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 1300;
    band.Q.value = 0.7;
    src.connect(band).connect(gain);

    const whistle = ctx.createOscillator();
    whistle.type = 'sine';
    whistle.frequency.value = 2400;
    whistle.detune.setValueAtTime(-40, t);
    whistle.detune.linearRampToValueAtTime(40, t + 1.5);
    const whistleGain = ctx.createGain();
    whistleGain.gain.value = 0.05;
    whistle.connect(whistleGain).connect(gain);

    src.start(t);
    whistle.start(t);

    rig.supersonic = { src, band, whistle, whistleGain, gain, stopping: false };
  }

  // -------------------------------------------------------------------------
  // One-shots
  // -------------------------------------------------------------------------

  /**
   * Ball contact. `strength` is 0..1; use `impactStrength()` on a closing speed
   * if that is what you have.
   *
   * Two layers: a filtered noise transient for the panel slap, and a pitched
   * body that glides down as it decays — the glide is what gives the ball its
   * size. Both scale from the same `impactParams` mapping so the whole
   * character moves together with strength.
   */
  playBallHit(strength: number, position?: Vec3 | null): void {
    const p = impactParams(strength);
    const shot = this.begin('sfx', p.gain, p.decay + 0.08, position);
    if (!shot) return;
    const { ctx, t0, out } = shot;

    out.gain.value = p.gain;

    const noiseSrc = this.noiseSource(ctx, 'white', 1);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(p.cutoffHz, t0);
    // The transient darkens as it dies, the way a real impact's brightness is
    // over in the first few milliseconds.
    lp.frequency.exponentialRampToValueAtTime(Math.max(200, p.cutoffHz * 0.25), t0 + p.decay * 0.5);
    lp.Q.value = 0.8;
    const noiseGain = ctx.createGain();
    this.percussive(noiseGain.gain, t0, p.noiseMix, 0.002, p.decay * 0.45);
    noiseSrc.connect(lp).connect(noiseGain).connect(out);

    const body = ctx.createOscillator();
    body.type = 'sine';
    body.frequency.setValueAtTime(p.bodyHz * 1.9, t0);
    body.frequency.exponentialRampToValueAtTime(p.bodyHz, t0 + p.decay * 0.6);
    const bodyGain = ctx.createGain();
    this.percussive(bodyGain.gain, t0, 1 - p.noiseMix * 0.5, 0.004, p.decay);
    body.connect(bodyGain).connect(out);

    const end = t0 + p.decay + 0.08;
    noiseSrc.start(t0, this.noiseOffset());
    noiseSrc.stop(end);
    body.start(t0);
    body.stop(end);
    this.arm(shot, [noiseSrc, body]);
  }

  /** Jump: the suspension unloading — a short low thump with a noise puff. */
  playJump(position?: Vec3 | null): void {
    const shot = this.begin('sfx', 0.5, 0.24, position);
    if (!shot) return;
    const { ctx, t0, out } = shot;
    out.gain.value = 0.5;

    const thump = ctx.createOscillator();
    thump.type = 'triangle';
    thump.frequency.setValueAtTime(220, t0);
    thump.frequency.exponentialRampToValueAtTime(90, t0 + 0.12);
    const thumpGain = ctx.createGain();
    this.percussive(thumpGain.gain, t0, 0.8, 0.003, 0.14);
    thump.connect(thumpGain).connect(out);

    const puffSrc = this.noiseSource(ctx, 'white', 1);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(1800, t0);
    bp.frequency.exponentialRampToValueAtTime(700, t0 + 0.1);
    bp.Q.value = 1.2;
    const puffGain = ctx.createGain();
    this.percussive(puffGain.gain, t0, 0.35, 0.002, 0.09);
    puffSrc.connect(bp).connect(puffGain).connect(out);

    const end = t0 + 0.24;
    thump.start(t0);
    thump.stop(end);
    puffSrc.start(t0, this.noiseOffset());
    puffSrc.stop(end);
    this.arm(shot, [thump, puffSrc]);
  }

  /**
   * Dodge: a whoosh. A bandpass swept up and back down across a noise bed —
   * the arc of the sweep is the entire sound, a static band just hisses.
   */
  playDodge(position?: Vec3 | null): void {
    const shot = this.begin('sfx', 0.45, 0.42, position);
    if (!shot) return;
    const { ctx, t0, out } = shot;
    out.gain.value = 0.45;

    const src = this.noiseSource(ctx, 'pink', 1.2);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 3.2;
    bp.frequency.setValueAtTime(420, t0);
    bp.frequency.exponentialRampToValueAtTime(2600, t0 + 0.13);
    bp.frequency.exponentialRampToValueAtTime(600, t0 + 0.36);

    const g = ctx.createGain();
    g.gain.setValueAtTime(FLOOR, t0);
    g.gain.exponentialRampToValueAtTime(0.9, t0 + 0.07);
    g.gain.exponentialRampToValueAtTime(FLOOR, t0 + 0.36);
    g.gain.setValueAtTime(0, t0 + 0.38);

    src.connect(bp).connect(g).connect(out);

    const end = t0 + 0.42;
    src.start(t0, this.noiseOffset());
    src.stop(end);
    this.arm(shot, [src]);
  }

  /**
   * Demolition: an explosion with a metallic cluster on top. The inharmonic
   * squares are the "car parts" — without them a filtered noise burst reads as
   * a door slam rather than a wreck.
   */
  playDemo(position?: Vec3 | null): void {
    const shot = this.begin('sfx', 0.95, 0.8, position);
    if (!shot) return;
    const { ctx, t0, out } = shot;
    out.gain.value = 0.95;

    const blast = this.noiseSource(ctx, 'white', 1);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(7000, t0);
    lp.frequency.exponentialRampToValueAtTime(220, t0 + 0.55);
    const blastGain = ctx.createGain();
    this.percussive(blastGain.gain, t0, 0.9, 0.004, 0.6);
    blast.connect(lp).connect(blastGain).connect(out);

    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(140, t0);
    sub.frequency.exponentialRampToValueAtTime(38, t0 + 0.35);
    const subGain = ctx.createGain();
    this.percussive(subGain.gain, t0, 0.85, 0.005, 0.4);
    sub.connect(subGain).connect(out);

    const sources: AudioScheduledSourceNode[] = [blast, sub];
    const shrapnel = ctx.createGain();
    this.percussive(shrapnel.gain, t0, 0.22, 0.002, 0.28);
    shrapnel.connect(out);
    // Ratios chosen to be deliberately inharmonic; consonant ones ring as a
    // chord instead of clattering.
    for (const ratio of [1, 1.41, 2.13]) {
      const o = ctx.createOscillator();
      o.type = 'square';
      o.frequency.value = 620 * ratio;
      o.detune.value = (this.rng() * 2 - 1) * 30;
      o.connect(shrapnel);
      sources.push(o);
    }

    const end = t0 + 0.8;
    blast.start(t0, this.noiseOffset());
    for (const s of sources) if (s !== blast) s.start(t0);
    for (const s of sources) s.stop(end);
    this.arm(shot, sources);
  }

  /**
   * Goal: the biggest sound in the game, and the only one that is allowed to be
   * long. Blast, a sub drop under it, and a slow pink swell standing in for the
   * crowd — the swell arrives after the blast, so the event has a shape.
   */
  playGoalExplosion(position?: Vec3 | null): void {
    const shot = this.begin('sfx', 1, 2.2, position);
    if (!shot) return;
    const { ctx, t0, out } = shot;
    out.gain.value = 1;

    const blast = this.noiseSource(ctx, 'white', 1);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(9000, t0);
    lp.frequency.exponentialRampToValueAtTime(160, t0 + 1.1);
    const blastGain = ctx.createGain();
    this.percussive(blastGain.gain, t0, 0.85, 0.005, 1.2);
    blast.connect(lp).connect(blastGain).connect(out);

    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(110, t0);
    sub.frequency.exponentialRampToValueAtTime(30, t0 + 0.9);
    const subGain = ctx.createGain();
    this.percussive(subGain.gain, t0, 1, 0.008, 1);
    sub.connect(subGain).connect(out);

    const crowd = this.noiseSource(ctx, 'pink', 0.9);
    const crowdBand = ctx.createBiquadFilter();
    crowdBand.type = 'bandpass';
    crowdBand.frequency.value = 900;
    crowdBand.Q.value = 0.5;
    const crowdGain = ctx.createGain();
    crowdGain.gain.setValueAtTime(FLOOR, t0);
    crowdGain.gain.exponentialRampToValueAtTime(0.35, t0 + 0.5);
    crowdGain.gain.exponentialRampToValueAtTime(FLOOR, t0 + 2.05);
    crowdGain.gain.setValueAtTime(0, t0 + 2.1);
    crowd.connect(crowdBand).connect(crowdGain).connect(out);

    const end = t0 + 2.2;
    blast.start(t0, this.noiseOffset());
    crowd.start(t0, this.noiseOffset());
    sub.start(t0);
    blast.stop(end);
    crowd.stop(end);
    sub.stop(end);
    this.arm(shot, [blast, sub, crowd]);
  }

  /** Landing. Dull and low compared with a ball hit, with a tyre chirp on top. */
  playLanding(strength: number, position?: Vec3 | null): void {
    const p = impactParams(strength);
    const shot = this.begin('sfx', p.gain * 0.7, p.decay + 0.06, position);
    if (!shot) return;
    const { ctx, t0, out } = shot;
    out.gain.value = p.gain * 0.7;

    const thud = ctx.createOscillator();
    thud.type = 'sine';
    thud.frequency.setValueAtTime(p.bodyHz * 1.4, t0);
    thud.frequency.exponentialRampToValueAtTime(p.bodyHz * 0.6, t0 + p.decay * 0.5);
    const thudGain = ctx.createGain();
    this.percussive(thudGain.gain, t0, 0.9, 0.003, p.decay * 0.8);
    thud.connect(thudGain).connect(out);

    const chirp = this.noiseSource(ctx, 'white', 1);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 2.5;
    // Tyre scrub is a narrow band well above the thud; capped so a heavy
    // landing does not turn into a whistle.
    bp.frequency.setValueAtTime(Math.min(3200, p.cutoffHz), t0);
    bp.frequency.exponentialRampToValueAtTime(700, t0 + p.decay * 0.6);
    const chirpGain = ctx.createGain();
    this.percussive(chirpGain.gain, t0, p.noiseMix * 0.5, 0.002, p.decay * 0.4);
    chirp.connect(bp).connect(chirpGain).connect(out);

    const end = t0 + p.decay + 0.06;
    thud.start(t0);
    thud.stop(end);
    chirp.start(t0, this.noiseOffset());
    chirp.stop(end);
    this.arm(shot, [thud, chirp]);
  }

  /** Boost pad pickup. Big pads get a second, higher note a fifth above. */
  playBoostPickup(isBig: boolean, position?: Vec3 | null): void {
    const spec = boostPickupTone(isBig);
    const shot = this.begin('ui', spec.gain, spec.duration + 0.05, position);
    if (!shot) return;
    const { ctx, t0, out } = shot;
    out.gain.value = spec.gain;

    const sources: AudioScheduledSourceNode[] = [];
    const end = t0 + spec.duration + 0.05;

    const voice = (frequency: number, delay: number, level: number): void => {
      const o = ctx.createOscillator();
      o.type = 'triangle';
      const start = t0 + delay;
      o.frequency.setValueAtTime(frequency, start);
      // A rising glide is the difference between "collected" and "beeped at".
      o.frequency.exponentialRampToValueAtTime(frequency * 2, start + spec.duration * 0.7);
      const g = ctx.createGain();
      this.percussive(g.gain, start, level, 0.006, spec.duration * 0.8);
      o.connect(g).connect(out);
      o.start(start);
      o.stop(end);
      sources.push(o);
    };

    voice(spec.frequency, 0, 1);
    if (isBig) voice(spec.frequency * 1.5, spec.duration * 0.35, 0.7);

    this.arm(shot, sources);
  }

  /** Countdown pip. Routed to the UI bus so it is not panned or distance-faded. */
  playCountdown(n: number): void {
    const spec = countdownTone(n);
    const shot = this.begin('ui', spec.gain, spec.duration + 0.05, null);
    if (!shot) return;
    const { ctx, t0, out } = shot;
    out.gain.value = spec.gain;

    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.value = spec.frequency;
    const og = ctx.createGain();
    this.percussive(og.gain, t0, 1, 0.005, spec.duration);
    o.connect(og).connect(out);

    // A little square underneath gives the pip an edge through a busy mix.
    const edge = ctx.createOscillator();
    edge.type = 'square';
    edge.frequency.value = spec.frequency * 2;
    const eg = ctx.createGain();
    this.percussive(eg.gain, t0, 0.12, 0.005, spec.duration * 0.5);
    edge.connect(eg).connect(out);

    const end = t0 + spec.duration + 0.05;
    o.start(t0);
    o.stop(end);
    edge.start(t0);
    edge.stop(end);
    this.arm(shot, [o, edge]);
  }

  /**
   * Referee whistle. Two close sines beat against each other and a fast LFO
   * modulates both — that flutter is the pea in the whistle, and without it two
   * sine tones are just two sine tones.
   */
  playWhistle(): void {
    const shot = this.begin('ui', 0.5, 0.62, null);
    if (!shot) return;
    const { ctx, t0, out } = shot;
    out.gain.value = 0.5;

    const g = ctx.createGain();
    g.gain.setValueAtTime(FLOOR, t0);
    g.gain.exponentialRampToValueAtTime(0.6, t0 + 0.02);
    g.gain.setValueAtTime(0.6, t0 + 0.42);
    g.gain.exponentialRampToValueAtTime(FLOOR, t0 + 0.55);
    g.gain.setValueAtTime(0, t0 + 0.57);
    g.connect(out);

    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 22;
    const lfoDepth = ctx.createGain();
    lfoDepth.gain.value = 45;
    lfo.connect(lfoDepth);

    const sources: AudioScheduledSourceNode[] = [lfo];
    for (const [frequency, level] of [
      [2650, 0.55],
      [2695, 0.4],
      [3980, 0.12],
    ] as const) {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = frequency;
      lfoDepth.connect(o.frequency);
      const og = ctx.createGain();
      og.gain.value = level;
      o.connect(og).connect(g);
      sources.push(o);
    }

    // A breath of noise under the tones; a whistle is blown, not struck.
    const air = this.noiseSource(ctx, 'white', 1);
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 2200;
    const airGain = ctx.createGain();
    airGain.gain.value = 0.06;
    air.connect(hp).connect(airGain).connect(g);

    const end = t0 + 0.62;
    for (const s of sources) s.start(t0);
    air.start(t0, this.noiseOffset());
    sources.push(air);
    for (const s of sources) s.stop(end);
    this.arm(shot, sources);
  }

  // -------------------------------------------------------------------------
  // Voice allocation
  // -------------------------------------------------------------------------

  /**
   * Allocate a voice and its output chain, stealing if the cap is reached.
   * Returns null when there is no audio graph, which is how every play method
   * becomes a no-op outside a browser.
   */
  private begin(
    busName: 'sfx' | 'engine' | 'ui',
    level: number,
    duration: number,
    position?: Vec3 | null
  ): Shot | null {
    const buses = this.mixer.ensure();
    if (!buses) return null;

    const ctx = buses.ctx;
    const now = ctx.currentTime;
    this.reap(now);
    if (this.voices.length >= this.maxVoices) {
      const victim = this.pickVictim(now);
      if (!victim) return null;
      this.steal(victim, now);
    }

    // Schedule a hair in the future: ramps that start exactly at currentTime
    // can land mid-block and lose their first segment.
    const t0 = now + 0.002;

    const out = ctx.createGain();
    let panner: PannerNode | null = null;
    if (position) {
      panner = this.makePanner(ctx);
      this.setPannerPosition(panner, position, now, false);
      out.connect(panner);
      panner.connect(buses[busName]);
    } else {
      out.connect(buses[busName]);
    }

    const voice: Voice = {
      id: this.nextVoiceId++,
      level: Math.max(0, Number.isFinite(level) ? level : 0),
      startedAt: now,
      endsAt: now + Math.max(0.01, Number.isFinite(duration) ? duration : 0.5),
      out,
      panner,
      sources: [],
      released: false,
    };
    this.voices.push(voice);

    return { ctx, t0, out, voice };
  }

  /** Register the voice's sources and release it once all of them have ended. */
  private arm(shot: Shot, sources: AudioScheduledSourceNode[]): void {
    const voice = shot.voice;
    voice.sources = sources;
    let pending = sources.length;
    if (pending === 0) {
      this.release(voice);
      return;
    }
    for (const s of sources) {
      s.onended = () => {
        pending -= 1;
        if (pending <= 0) this.release(voice);
      };
    }
  }

  /**
   * Steal ranking: nominal level scaled by how much of the voice is left to
   * play. That prefers a quiet sound already three-quarters finished over a
   * loud one that just started, which is the ordering a listener would pick —
   * plain "oldest" would cut the goal explosion off under a burst of taps.
   */
  private pickVictim(now: number): Voice | null {
    let best: Voice | null = null;
    let bestScore = Infinity;
    for (const v of this.voices) {
      const span = Math.max(1e-3, v.endsAt - v.startedAt);
      const remaining = Math.max(0, v.endsAt - now) / span;
      const score = v.level * remaining;
      if (score < bestScore || (score === bestScore && best && v.startedAt < best.startedAt)) {
        bestScore = score;
        best = v;
      }
    }
    return best;
  }

  private steal(voice: Voice, now: number): void {
    try {
      const g = voice.out.gain;
      g.cancelScheduledValues(now);
      g.setValueAtTime(g.value, now);
      g.linearRampToValueAtTime(0, now + STEAL_FADE);
      for (const s of voice.sources) s.stop(now + STEAL_FADE);
    } catch {
      // A source that already stopped throws on a second stop(); harmless.
    }
    this.release(voice);
  }

  /** Idempotent. Disconnects the chain and drops the voice from the pool. */
  private release(voice: Voice): void {
    if (voice.released) return;
    voice.released = true;
    const i = this.voices.indexOf(voice);
    if (i >= 0) this.voices.splice(i, 1);
    try {
      voice.out.disconnect();
      // The panner is downstream of `out` but still connected to the bus, so
      // it has to be released explicitly or it stays in the graph forever.
      voice.panner?.disconnect();
    } catch {
      // Already disconnected.
    }
  }

  /** Drop voices whose scheduled end has passed but whose `onended` was missed
   *  (a suspended context never fires it). */
  private reap(now: number): void {
    for (const v of [...this.voices]) {
      if (v.endsAt + 0.25 < now) this.release(v);
    }
  }

  // -------------------------------------------------------------------------
  // Shared node construction
  // -------------------------------------------------------------------------

  /**
   * Attack/decay envelope on a gain param, built from exponential ramps.
   * Exponentials are used throughout rather than linear ramps because a linear
   * decay to zero sounds like a fade-out, not like something stopping ringing.
   */
  private percussive(
    param: AudioParam,
    t0: number,
    peak: number,
    attack: number,
    decay: number
  ): void {
    const p = Math.max(FLOOR * 2, Number.isFinite(peak) ? peak : 0);
    const a = Math.max(0.001, attack);
    const d = Math.max(0.005, decay);
    param.setValueAtTime(FLOOR, t0);
    param.exponentialRampToValueAtTime(p, t0 + a);
    param.exponentialRampToValueAtTime(FLOOR, t0 + a + d);
    param.setValueAtTime(0, t0 + a + d);
  }

  private makePanner(ctx: AudioContext): PannerNode {
    const p = ctx.createPanner();
    // equalpower rather than HRTF: with a 16-voice cap, HRTF convolution is the
    // single most expensive thing in the audio thread, and the arena is viewed
    // from outside rather than in first person, so the extra cue is wasted.
    p.panningModel = 'equalpower';
    p.distanceModel = 'inverse';
    // Units are uu, so these are car-lengths, not metres. refDistance is about
    // half the width of the goal: closer than that and there is no attenuation.
    p.refDistance = 900;
    p.maxDistance = 14000;
    p.rolloffFactor = 0.85;
    return p;
  }

  private setPannerPosition(p: PannerNode, v: Vec3, when: number, smooth: boolean): void {
    const t = toThree(v);
    const px = (p as unknown as { positionX?: AudioParam }).positionX;
    if (px) {
      const a = p as unknown as Record<string, AudioParam>;
      if (smooth) {
        a.positionX.setTargetAtTime(t.x, when, 0.03);
        a.positionY.setTargetAtTime(t.y, when, 0.03);
        a.positionZ.setTargetAtTime(t.z, when, 0.03);
      } else {
        a.positionX.setValueAtTime(t.x, when);
        a.positionY.setValueAtTime(t.y, when);
        a.positionZ.setValueAtTime(t.z, when);
      }
      return;
    }
    (p as unknown as { setPosition?: (x: number, y: number, z: number) => void }).setPosition?.(
      t.x,
      t.y,
      t.z
    );
  }

  /**
   * The two noise loops, generated once and shared by every voice. Seeded, so
   * two sessions produce identical buffers, and loop-crossfaded so a sustained
   * layer does not tick once per pass.
   */
  private noiseBuffer(ctx: AudioContext, kind: NoiseKind): AudioBuffer {
    const cached = this.buffers.get(kind);
    if (cached) return cached;

    const frames = Math.floor(ctx.sampleRate * NOISE_SECONDS);
    const data = loopableNoise(kind, frames, this.seed + (kind === 'pink' ? 1 : 0), NOISE_LOOP_FADE);
    const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
    // set() rather than copyToChannel(): identical result, and it does not care
    // which flavour of ArrayBuffer backs the generated array.
    buffer.getChannelData(0).set(data);
    this.buffers.set(kind, buffer);
    return buffer;
  }

  /**
   * A looping source over a shared noise buffer. `rate` shifts the spectrum,
   * which is how two layers cut from the same buffer avoid correlating into
   * one flat hiss.
   */
  private noiseSource(ctx: AudioContext, kind: NoiseKind, rate: number): AudioBufferSourceNode {
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer(ctx, kind);
    src.loop = true;
    src.playbackRate.value = rate;
    return src;
  }

  /** Random read offset, so repeated one-shots do not replay the same samples. */
  private noiseOffset(): number {
    return this.rng() * NOISE_SECONDS;
  }

  // -------------------------------------------------------------------------
  // Engine rig
  // -------------------------------------------------------------------------

  /**
   * Build the continuous layers once, on first use.
   *
   * The engine is two oscillators, not one. Each runs a PeriodicWave built from
   * ENGINE_PARTIALS: one with the idle mix (heavy half-order lope, steep
   * roll-off) and one with the loaded mix (flatter, brighter). Crossfading
   * between them tracks the timbre change with load, which a single wave and a
   * moving filter cannot do — a filter can only take away what is already
   * there, and at idle the upper partials must not be there at all.
   *
   * They are also detuned against each other, so the two banks beat. That
   * beating, the half-order partial, the noise bed and the pitch jitter applied
   * in updateEngine are between them the whole difference between an engine and
   * a sawtooth.
   */
  private ensureRig(): Rig | null {
    if (this.rig) return this.rig;
    const buses = this.mixer.ensure();
    if (!buses) return null;

    const ctx = buses.ctx;
    const t = ctx.currentTime;

    const panner = this.makePanner(ctx);
    panner.connect(buses.engine);

    const input = ctx.createGain();
    input.gain.value = 1;
    input.connect(panner);

    const engineGain = ctx.createGain();
    engineGain.gain.value = 0;
    engineGain.connect(input);

    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 600;
    lowpass.Q.value = 0.9;
    lowpass.connect(engineGain);

    const idleWave = this.buildEngineWave(ctx, 0, 0);
    const loadWave = this.buildEngineWave(ctx, 1, 1);

    const gainIdle = ctx.createGain();
    gainIdle.gain.value = 1;
    gainIdle.connect(lowpass);
    const oscIdle = ctx.createOscillator();
    oscIdle.setPeriodicWave(idleWave);
    oscIdle.frequency.value = 36;
    oscIdle.connect(gainIdle);

    const gainLoad = ctx.createGain();
    gainLoad.gain.value = 0;
    gainLoad.connect(lowpass);
    const oscLoad = ctx.createOscillator();
    oscLoad.setPeriodicWave(loadWave);
    oscLoad.frequency.value = 36;
    oscLoad.detune.value = 9;
    oscLoad.connect(gainLoad);

    const roughGain = ctx.createGain();
    roughGain.gain.value = 0;
    roughGain.connect(engineGain);
    const roughFilter = ctx.createBiquadFilter();
    roughFilter.type = 'bandpass';
    roughFilter.frequency.value = 300;
    roughFilter.Q.value = 1.6;
    roughFilter.connect(roughGain);
    const roughSrc = this.noiseSource(ctx, 'pink', 1);
    roughSrc.connect(roughFilter);

    oscIdle.start(t);
    oscLoad.start(t);
    roughSrc.start(t, this.noiseOffset());

    this.rig = {
      ctx,
      input,
      panner,
      oscIdle,
      gainIdle,
      oscLoad,
      gainLoad,
      lowpass,
      engineGain,
      roughSrc,
      roughFilter,
      roughGain,
      boost: null,
      supersonic: null,
    };
    return this.rig;
  }

  /**
   * Realize the harmonic mix from ./synth as a PeriodicWave.
   *
   * ENGINE_PARTIALS are multiples of the fundamental, and every one of them is
   * a whole multiple of 0.5 — so the wave is built on the HALF-order partial and
   * the oscillator is run at half the fundamental. That is what buys the
   * four-stroke lope for free instead of costing a second oscillator.
   */
  private buildEngineWave(ctx: AudioContext, throttle: number, load: number): PeriodicWave {
    const tone = engineTone(throttle, load * ENGINE_TOP_SPEED, false);
    let maxIndex = 1;
    for (const ratio of ENGINE_PARTIALS) maxIndex = Math.max(maxIndex, Math.round(ratio * 2));

    const real = new Float32Array(maxIndex + 1);
    const imag = new Float32Array(maxIndex + 1);
    ENGINE_PARTIALS.forEach((ratio, i) => {
      imag[Math.round(ratio * 2)] = tone.harmonics[i];
    });
    return ctx.createPeriodicWave(real, imag, { disableNormalization: false });
  }

  private killBoost(rig: Rig): void {
    const layer = rig.boost;
    if (!layer) return;
    rig.boost = null;
    try {
      layer.hissSrc.onended = null;
      layer.hissSrc.stop();
      layer.rumbleSrc.stop();
    } catch {
      // Already stopped.
    }
    layer.gain.disconnect();
  }

  private killSupersonic(rig: Rig): void {
    const layer = rig.supersonic;
    if (!layer) return;
    rig.supersonic = null;
    try {
      layer.src.onended = null;
      layer.src.stop();
      layer.whistle.stop();
    } catch {
      // Already stopped.
    }
    layer.gain.disconnect();
  }

  private teardownRig(): void {
    const rig = this.rig;
    if (!rig) return;
    this.killBoost(rig);
    this.killSupersonic(rig);
    this.rig = null;
    try {
      rig.oscIdle.stop();
      rig.oscLoad.stop();
      rig.roughSrc.stop();
    } catch {
      // Already stopped.
    }
    rig.input.disconnect();
    rig.panner.disconnect();
  }
}

// A process-wide engine, created on first request rather than at import.
let shared: AudioEngine | null = null;

export const sharedAudio = (): AudioEngine => (shared ??= new AudioEngine());

export const disposeSharedAudio = (): void => {
  shared?.dispose();
  shared = null;
};

export { impactStrength };
